/**
 * ParkDetails screen displays detailed information about a specific park.
 *
 * @module ParkDetails
 * @param {Object} props - Component props.
 * @param {Object} props.route - React Navigation route object.
 * @param {Object} props.route.params - Route parameters.
 * @param {string} props.route.params.park_name - Name of the park to display.
 * @returns {JSX.Element} Park details screen component.
 *
 * @example
 * <ParkDetails route={{ params: { park_name: "Queen City Park" } }} />
 *
 * @description
 * This component renders the details of a park, including its name, address, amenities, hours,
 * rental and adoption status, description, and user reviews. It uses custom Chip components and
 * FontAwesome icons for visual representation. The park data is currently hardcoded for demonstration.
 */
import { useEffect, useCallback, useRef, useMemo } from "react";
import { ScrollView, View, StyleSheet, Text, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";
import ParksStore from "../storage/parksStore";
import { COLORS } from "../theme/colors";
import STRINGS from "../constants/strings";
import DetailsMapView from "../components/ParksDetailsMap/DetailsMapView";
import ParkDetailsHeaderButtons from "../components/ParkDetailsComponents/ParkDetailsHeaderButtons";
import ParkDetailsChips from "../components/ParkDetailsComponents/ParkDetailsChips";
import ParkDetailsReviews from "../components/ParkDetailsComponents/ParkDetailsReviews";
import ImageCarousel from "../components/ImageCarousel/ImageCarousel";
import { openDirections, handleLinkingPress } from "../utils/helper";
import { isMobileOrMobileWeb, screenHeight, platform } from "../platform";
import MapEmbedScreen from "../components/MapEmbedScreen";

const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

export default function ParksDetails({ route }) {
  const getLocationDetails = ParksStore((state) => state.getLocationDetails);
  const resetLocationDetails = ParksStore((state) => state.resetLocationDetails);
  const parkData = ParksStore((state) => state.locationDetails);
  const googleReviews = ParksStore((state) => state.googleReviews);
  const scrollViewRef = useRef(null);
  const reviewsRef = useRef(null);


  const { park_name } = route.params;
  const navigation = useNavigation();
  const canUsePhone = useMemo(() => isMobileOrMobileWeb(), []);

  const HeaderButtons = useCallback(() => <ParkDetailsHeaderButtons parkData={parkData} />, [parkData]);
  const renderPhoneWrapper = useCallback(
    (children) => <Pressable onPress={() => handleLinkingPress(`tel:${parkData.phoneNumber}`)}>{children}</Pressable>,
    [parkData]
  );

  const detailsTop = useMemo(() => {
    if (parkData?.attachments && parkData.attachments.length > 0) {
      return <ImageCarousel images={parkData.attachments} />;
    } else if (parkData?.latlng) {
      return platform === "native" ? (
        <MapEmbedScreen style={styles.mapContainer} lat={parkData.latlng[0]} lng={parkData.latlng[1]} />
      ) : (
        <View style={styles.mapContainer}>
          <DetailsMapView name={parkData?.title} lat={parkData.latlng[0]} lng={parkData.latlng[1]} />
        </View>
      );
    } else {
      return null;
    }
  }, [parkData]);

  useEffect(() => {
    const fetchData = async () => {
      resetLocationDetails();
      try {
        await getLocationDetails(park_name);
      } catch (error) {
        console.log("Error fetching park details:", error);
      }
    };

    fetchData();
  }, [getLocationDetails, park_name, resetLocationDetails]);

  useEffect(() => {
    if (parkData === null) {
      return;
    }

    navigation.setOptions({
      headerRight: HeaderButtons,
      headerRightContainerStyle: styles.headerRight,
    });
  }, [parkData, HeaderButtons, navigation]);

  const chipOnPress = useMemo(
    () => ({
      scrollToReviews: () => {
        reviewsRef.current?.measureLayout(scrollViewRef.current, (x, y) => {
          scrollViewRef.current.scrollTo({ y, animated: true });
        });
      },
      openRentals: () => {
        handleLinkingPress(STRINGS.parksRentalLink);
      },
      openAdoption: () => {
        handleLinkingPress(STRINGS.parksAdoptionLink);
      },
      openMaps: (lat, lng) => {
        openDirections(lat, lng);
      },
    }),
    []
  );

  return parkData != null ? (
    <ScrollView ref={scrollViewRef} contentContainerStyle={styles.scrollContent}>
      <View style={styles.contentWrapper}>
        {detailsTop}
        <Text
          style={styles.title}
          accessible={true}
          accessibilityRole="header"
          accessibilityLabel={`Park name: ${decodeURI(park_name)}`}
        >
          {parkData?.title}
        </Text>
        <View style={styles.infoContainer}>
          <ParkDetailsChips parkData={parkData} googleReviews={googleReviews} onPress={chipOnPress} />

          {parkData?.address && (
            <View style={styles.additionalInfoContainer}>
              <Text style={styles.infoIconTitle} accessibilityRole="header">
                {STRINGS.location}:
              </Text>
              <Pressable onPress={() => openDirections(parkData?.latlng?.[0], parkData?.latlng?.[1])}>
                <Text
                  style={[styles.infoText, styles.infoTextButton]}
                  accessible={true}
                  accessibilityRole="text"
                  accessibilityLabel={`Address: ${parkData.address}, VA ${parkData.zip}`}
                >
                  {parkData.address}
                  {parkData.zip && `, VA ${parkData.zip}`}
                </Text>
              </Pressable>
            </View>
          )}

          {parkData?.hours && (
            <View style={styles.additionalInfoContainer}>
              <Text style={styles.infoIconTitle} accessibilityRole="header">
                {STRINGS.hours}:
              </Text>
              <Text
                style={styles.infoText}
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={`Operating hours: ${parkData.hours}`}
              >
                {parkData.hours}
              </Text>
            </View>
          )}

          {parkData?.phoneNumber && (
            <View style={styles.additionalInfoContainer}>
              <Text style={styles.infoIconTitle} accessibilityRole="header">
                {STRINGS.phone}:
              </Text>
              <ConditionalWrapper condition={canUsePhone} wrapper={renderPhoneWrapper}>
                <Text
                  style={[styles.infoText, canUsePhone && styles.infoTextButton]}
                  accessible={true}
                  accessibilityRole="text"
                  accessibilityLabel={`Phone number: ${parkData.phoneNumber}`}
                >
                  {parkData.phoneNumber}
                </Text>
              </ConditionalWrapper>
            </View>
          )}

          {parkData?.mappedAmenities && (
            <View style={styles.additionalInfoContainer}>
              <Text style={styles.infoIconTitle} accessibilityRole="header">
                {STRINGS.amenities}:
              </Text>
              <Text
                style={styles.infoText}
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={`Amenities selected: ${parkData.mappedAmenities.join(", ")}`}
              >
                {parkData.mappedAmenities.join(", ")}
              </Text>
            </View>
          )}

          {parkData?.notes && (
            <View style={styles.additionalInfoContainer}>
              <Text style={styles.infoIconTitle} accessibilityRole="header">
                {STRINGS.notes}:
              </Text>
              <Text
                style={styles.infoText}
                accessible={true}
                accessibilityRole="text"
                accessibilityLabel={`Notes: ${parkData.notes}`}
              >
                {parkData.notes}
              </Text>
            </View>
          )}
        </View>
        <View style={styles.divider} />
        <ParkDetailsReviews ref={reviewsRef} googleReviews={googleReviews} />
      </View>
    </ScrollView>
  ) : null;
}

const styles = StyleSheet.create({
  scrollContent: {
    paddingBottom: 30,
  },
  contentWrapper: {
    width: "100%",
    maxWidth: 750,
    alignSelf: "center",
  },
  mapContainer: {
    height: 200,
    marginBottom: 10,
  },
  carouselLoadingContainer: {
    height: screenHeight * 0.25,
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 10,
    paddingHorizontal: 15,
  },
  infoContainer: {
    flexDirection: "column",
    paddingHorizontal: 15,
  },
  additionalInfoContainer: {
    flexDirection: "column",
    gap: 4,
    marginTop: 20,
  },
  infoIconTitle: {
    fontSize: 16,
    fontWeight: "bold",
  },
  infoText: {
    fontSize: 14,
  },
  infoTextButton: {
    color: COLORS.greenLink,
    textDecorationLine: "underline",
  },
  divider: {
    height: 1,
    backgroundColor: COLORS.tertiary,
    marginTop: 20,
    marginHorizontal: 15,
    alignSelf: "stretch",
  },
  headerRight: {
    paddingRight: 15,
  },
});
